import { useState, useMemo, useRef, useEffect } from 'react';
import { formatDisplayDate } from '../../utils/format';
import { useUserTimeOffs, useTimeOffTypes, useCreateTimeOff, useUpdateTimeOff, useSubmitTimeOff, useCancelTimeOff, useTeamTimeOffs, useMyVacationBalance, useMyTimeOffAllowances } from '../../hooks/useTimeOff';
import { useAuth } from '../../services/authContext';
import { VacationBalanceCard } from '../../components/time-off/VacationBalanceCard';
import { formatBalanceHours, hasVacationEntitlement, storedHoursToReal } from '../../utils/vacationBalance';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import TimeOffForm from '../../components/domain/TimeOffForm';
import { TimeOffCalendar } from '../../components/time-off/TimeOffCalendar';
import type { TimeOff, CreateTimeOffInput } from '../../services/api/timeOff';

// Filter removed — always fetch all states

export default function TimeOffListPage() {
  // todayStr in deps ensures the memo recomputes on day boundary
  // (same fix applied in F-2203 for capacity/dashboard useMemo([]) stale-date captures).
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const [showForm, setShowForm] = useState(false);
  const [editingTimeOff, setEditingTimeOff] = useState<TimeOff | null>(null);
  const [confirmSubmitId, setConfirmSubmitId] = useState<number | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null);
  // F2-005: surface mutation rejections inline instead of leaking as unhandled
  // promise rejections (which left ConfirmModals stuck open / the form hung).
  const [actionError, setActionError] = useState<string | null>(null);

  // Edit form is rendered above the calendar/list; when the user triggers edit
  // from a list row or calendar double-click far down the page, scroll the form
  // into view so they don't have to chase it manually (MKS-97).
  const editFormRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editingTimeOff) {
      editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingTimeOff]);

  const { user } = useAuth();

  // MKS-326 — the employee's own balance.
  const { data: balance, isLoading: balanceLoading } = useMyVacationBalance(now.getFullYear());
  /**
   * The conversion factor for the hour figures in the list below. `TimeOff`
   * rows carry stored fraction-of-standard-day hours (8 = one whole working
   * day), which read as somebody else's hours for anyone not at 1.0 FTE.
   * `undefined` leaves the rows printing the raw stored figure, as before.
   *
   * This is the requester's CURRENT day length, so a request spanning an FTE
   * change is approximated here; the balance card above is computed per day
   * and is exact.
   */
  const dayHours = hasVacationEntitlement(balance) ? balance.dailyWorkingHours : undefined;

  // MKS-327 — the remaining days on every type with its own yearly cap, so the
  // request form can show "3 of 5 sick days left" and warn before it is
  // overdrawn. Empty while the flag is off, so this costs nothing until then.
  const { data: allowances } = useMyTimeOffAllowances(now.getFullYear());

  const { data: timeOffs, isLoading, error } = useUserTimeOffs({});
  const { data: teamTimeOffs } = useTeamTimeOffs();
  const { data: types } = useTimeOffTypes();
  const createMutation = useCreateTimeOff();
  const updateMutation = useUpdateTimeOff();
  const submitMutation = useSubmitTimeOff();
  const cancelMutation = useCancelTimeOff();

  // Merge user's own time-offs with team approved time-offs for the calendar
  const calendarTimeOffs = useMemo((): TimeOff[] => {
    const ownIds = new Set((timeOffs ?? []).map((t) => t.id));
    // Convert team time-offs to TimeOff shape (minimal, for calendar display)
    const teamEntries: TimeOff[] = (teamTimeOffs ?? [])
      .filter((t) => !ownIds.has(t.id))
      .map((t) => ({
        id: t.id,
        userId: t.userId,
        user: { id: t.userId, email: '', firstName: t.userName.split(' ')[0] ?? null, lastName: t.userName.split(' ').slice(1).join(' ') || null },
        timeOffType: null,
        dateFrom: t.dateFrom,
        dateTo: t.dateTo,
        dateFromHours: 8,
        dateToHours: 8,
        state: 'approved',
        totalDays: 0,
        totalHours: 0,
        days: [],
        createdAt: null,
        updatedAt: null,
      }));
    return [...(timeOffs ?? []), ...teamEntries];
  }, [timeOffs, teamTimeOffs]);

  // Show open/submitted regardless of date (user must still act on them, even if past),
  // and any future-or-current approved/rejected/cancelled. Without this, an open time-off
  // whose dateTo has passed disappears from the list and the user can no longer submit
  // or delete it (MKS-97).
  const visibleTimeOffs = useMemo(() => {
    return timeOffs?.filter((t) => {
      if (t.state === 'open' || t.state === 'submitted' || t.state === 'rejected') return true;
      return t.dateTo && t.dateTo >= todayStr;
    });
  }, [timeOffs, todayStr]);

  const handleCreate = async (input: CreateTimeOffInput) => {
    setActionError(null);
    try {
      const created = await createMutation.mutateAsync(input);
      await submitMutation.mutateAsync(created.id);
      setShowForm(false);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to create time off request');
    }
  };

  const handleUpdate = async (input: CreateTimeOffInput) => {
    if (!editingTimeOff) return;
    setActionError(null);
    try {
      await updateMutation.mutateAsync({ id: editingTimeOff.id, input });
      setEditingTimeOff(null);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to update time off request');
    }
  };

  const handleSubmit = async () => {
    if (confirmSubmitId === null) return;
    setActionError(null);
    try {
      await submitMutation.mutateAsync(confirmSubmitId);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to submit time off request');
    } finally {
      setConfirmSubmitId(null);
    }
  };

  const handleCancel = async () => {
    if (confirmCancelId === null) return;
    setActionError(null);
    try {
      await cancelMutation.mutateAsync(confirmCancelId);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to cancel time off request');
    } finally {
      setConfirmCancelId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load time off requests: {(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Time Off</h1>
        <Button onClick={() => setShowForm(true)}>
          New Request
        </Button>
      </div>

      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* Vacation balance (MKS-326) — renders nothing without an entitlement */}
      <VacationBalanceCard balance={balance} isLoading={balanceLoading} />

      {/* Create Form */}
      {showForm && (
        <TimeOffForm
          types={types || []}
          balance={balance}
          allowances={allowances}
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Edit Form */}
      {editingTimeOff && (
        <div ref={editFormRef}>
          <TimeOffForm
            types={types || []}
            balance={balance}
            allowances={allowances}
            mode="edit"
            // MKS-361 — lets the coverage block show the substitutes already
            // stored on this request, including ones an approver set.
            timeOffId={editingTimeOff.id}
            initialValues={{
              timeOffTypeId: editingTimeOff.timeOffType?.id,
              dateFrom: editingTimeOff.dateFrom ?? '',
              dateTo: editingTimeOff.dateTo ?? '',
              dateFromHours: editingTimeOff.dateFromHours,
              dateToHours: editingTimeOff.dateToHours,
            }}
            onSubmit={handleUpdate}
            onCancel={() => setEditingTimeOff(null)}
            isLoading={updateMutation.isPending}
          />
        </div>
      )}

      {/* Calendar View */}
      {calendarTimeOffs.length > 0 && (
        <TimeOffCalendar
          timeOffs={calendarTimeOffs}
          currentUserId={user?.id}
          onTimeOffDoubleClick={(id) => {
            const t = timeOffs?.find((x) => x.id === id);
            if (t && (t.state === 'open' || t.state === 'submitted' || t.state === 'rejected')) {
              setEditingTimeOff(t);
            }
          }}
        />
      )}

      {/* List — actionable + upcoming time-offs */}
      {!visibleTimeOffs || visibleTimeOffs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No time off requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleTimeOffs.map((timeOff) => {
            const isEditable = timeOff.state === 'open' || timeOff.state === 'submitted' || timeOff.state === 'rejected';
            return (
              <div
                key={timeOff.id}
                onDoubleClick={() => isEditable && setEditingTimeOff(timeOff)}
                className={`bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between ${isEditable ? 'cursor-pointer' : ''}`}
                title={isEditable ? 'Double-click to edit' : undefined}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">
                      {timeOff.timeOffType?.name ?? 'Time Off'}
                    </span>
                    <StatusBadge state={timeOff.state} size="sm" />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatDisplayDate(timeOff.dateFrom)} - {formatDisplayDate(timeOff.dateTo)}
                    <span className="ml-2 text-gray-400">
                      ({timeOff.totalDays} {timeOff.totalDays === 1 ? 'day' : 'days'},{' '}
                      {dayHours === undefined
                        ? `${timeOff.totalHours}h`
                        : `${formatBalanceHours(storedHoursToReal(timeOff.totalHours, dayHours))}h`}
                      )
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isEditable && (
                    <Button variant="secondary" size="sm" onClick={() => setEditingTimeOff(timeOff)}>
                      Edit
                    </Button>
                  )}
                  {timeOff.state === 'open' && (
                    <Button size="sm" onClick={() => setConfirmSubmitId(timeOff.id)}>
                      Submit
                    </Button>
                  )}
                  {['open', 'submitted', 'approved'].includes(timeOff.state) && (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmCancelId(timeOff.id)} className="text-red-600">
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit Confirmation */}
      <ConfirmModal
        isOpen={confirmSubmitId !== null}
        onClose={() => setConfirmSubmitId(null)}
        onConfirm={handleSubmit}
        title="Submit Time Off Request"
        message="Are you sure you want to submit this time off request for approval?"
        confirmText="Submit"
        isLoading={submitMutation.isPending}
      />

      {/* Cancel Confirmation */}
      <ConfirmModal
        isOpen={confirmCancelId !== null}
        onClose={() => setConfirmCancelId(null)}
        onConfirm={handleCancel}
        title="Delete Time Off Request"
        message="Are you sure you want to delete this time off request?"
        confirmText="Delete"
        variant="danger"
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
}
